// nuolino-admin/src/components/layout/CommandPalette.tsx
// Command Palette global (Cmd/Ctrl+K). Busca em pacientes (via BFF) +
// acoes rapidas (ir para dashboard, criar convite, etc).
//
// Design:
//   - Dialog com overlay escuro (alpha).
//   - Input de busca no topo, lista de resultados abaixo.
//   - Setas para navegar, Enter para selecionar, Esc para fechar.
//   - Fecha ao clicar fora ou em um resultado.
//   - Filtragem client-side (ate 50 items) — para datasets grandes,
//     trocar por API search (T2.13.26).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FileText,
  Wallet,
  Globe,
  Bell,
  type LucideIcon,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { listPatients, type Patient } from '@/features/patients/patients-api';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  action: () => void;
  group: 'navigation' | 'patient' | 'action';
}

export interface CommandPaletteProps {
  onClose: () => void;
}

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Esc fecha. (Cmd/Ctrl+K é tratado no AppShell para nao duplicar.)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Foco no input ao montar.
  useEffect(() => {
    setQuery('');
    setHighlight(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Busca pacientes (so carrega quando a palette abre).
  const { data: patientsData } = useQuery({
    queryKey: ['patients-cmd', { page: 1, pageSize: 50 }] as const,
    queryFn: ({ signal }) => listPatients({ page: 1, pageSize: 50 }, signal),
    enabled: true,
    staleTime: 30_000,
  });

  const patients: Patient[] = useMemo(
    () => patientsData?.rows ?? [],
    [patientsData],
  );

  const items: CommandItem[] = useMemo(() => {
    const navigateAndClose = (path: string) => () => {
      onClose();
      navigate(path);
    };
    const navItems: CommandItem[] = [
      { id: 'nav.dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, action: navigateAndClose('/dashboard'), group: 'navigation' },
      { id: 'nav.patients', label: t('nav.patients'), icon: Users, action: navigateAndClose('/patients'), group: 'navigation' },
      { id: 'nav.consultations', label: t('nav.consultations'), icon: FileText, action: navigateAndClose('/consultations'), group: 'navigation' },
      { id: 'nav.protocols', label: t('nav.protocols'), icon: FileText, action: navigateAndClose('/protocols'), group: 'navigation' },
      { id: 'nav.receipts', label: t('nav.receipts'), icon: Wallet, action: navigateAndClose('/receipts'), group: 'navigation' },
      { id: 'nav.public_profile', label: t('nav.public_profile'), icon: Globe, action: navigateAndClose('/public-profile'), group: 'navigation' },
      { id: 'nav.notifications', label: t('nav.notifications'), icon: Bell, action: navigateAndClose('/notifications'), group: 'navigation' },
      { id: 'action.invite', label: t('patients.new_invite'), icon: UserPlus, action: navigateAndClose('/patients/invite'), group: 'action' },
    ];
    const patientItems: CommandItem[] = patients.map((p) => ({
      id: `p-${p.id}`,
      label: p.name,
      hint: p.email,
      icon: Users,
      action: navigateAndClose(`/patients/${p.id}`),
      group: 'patient',
    }));
    return [...navItems, ...patientItems];
  }, [t, navigate, patients, onClose]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        i.hint?.toLowerCase().includes(q),
    );
  }, [items, query]);

  // Mantem highlight em range valido.
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(Math.max(0, filtered.length - 1));
  }, [filtered.length, highlight]);

  // Scroll automatico no item destacado.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmd-index="${highlight}"]`);
    // jsdom nao implementa scrollIntoView; proteger para nao quebrar testes.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  function onKeyDownInput(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[highlight];
      item?.action();
    }
  }

  // Agrupa por categoria para a UI.
  const groups: { label: string; items: CommandItem[] }[] = [
    { label: t('common.menu'), items: filtered.filter((i) => i.group === 'navigation') },
    { label: t('nav.patients'), items: filtered.filter((i) => i.group === 'patient') },
    { label: t('common.search'), items: filtered.filter((i) => i.group === 'action') },
  ].filter((g) => g.items.length > 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-modal flex items-start justify-center bg-neutral-900/40 px-4 pt-[10vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-xl"
        role="combobox"
        aria-expanded="true"
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
          <Search className="h-4 w-4 text-text-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDownInput}
            placeholder={t('common.search_placeholder')}
            aria-label={t('common.search_placeholder')}
            className="flex-1 bg-transparent text-base text-text outline-none placeholder:text-text-muted"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="rounded border border-border-subtle bg-surface-alt px-1.5 py-0.5 text-xs text-text-muted">
            Esc
          </kbd>
        </div>

        <div
          ref={listRef}
          role="listbox"
          className="max-h-80 overflow-y-auto py-1"
          aria-label="Resultados"
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-text-muted">
              {t('common.empty')}
            </div>
          ) : (
            groups.map((g, gi) => (
              <div key={g.label} className={gi > 0 ? 'mt-1 border-t border-border-subtle pt-1' : ''}>
                <p className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-text-muted">
                  {g.label}
                </p>
                {g.items.map((item) => {
                  const idx = filtered.indexOf(item);
                  const isHighlight = idx === highlight;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      data-cmd-index={idx}
                      aria-selected={isHighlight}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={item.action}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                        isHighlight ? 'bg-primary-50 text-primary-800' : 'text-text hover:bg-surface-alt',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.hint && (
                        <span className="hidden truncate text-xs text-text-muted sm:inline">{item.hint}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
